import React from 'react';
import { Accept, useDropzone } from 'react-dropzone';
import {
  Box,
  Typography,
  IconButton,
  Grid2,
  alpha,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface ImageUploaderComponentProps {
  initialPreviewUrls?: string[];
  onDrop: (acceptedFiles: File[]) => void;
  onRemove?: (index: number) => void;
  maxFileSize?: number;
  acceptedFiles?: Accept;
  maxFiles?: number;
  isDisabled?: boolean;
}

const ImageUploaderComponent = ({
  initialPreviewUrls = [],
  onDrop,
  onRemove,
  maxFileSize = 5242880,
  acceptedFiles,
  maxFiles = 1,
  isDisabled = false,
}: ImageUploaderComponentProps) => {
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    fileRejections,
  } = useDropzone({
    onDrop,
    maxSize: maxFileSize,
    maxFiles: maxFiles,
    multiple: maxFiles > 1,
    accept: acceptedFiles ?? { 'image/jpeg': [], 'image/png': [] },
    disabled: isDisabled,
  });
  const theme = useTheme();

  const handleRemoveImage = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    if (onRemove) {
      onRemove(index);
    }
  };

  const isTooManyFilesError = fileRejections.some((rejection) =>
    rejection.errors.some((error) => error.code === 'too-many-files'),
  );

  return (
    <Box>
      {initialPreviewUrls && initialPreviewUrls.length > 0 && (
        <Grid2 container spacing={2} sx={{ mb: 2 }}>
          {initialPreviewUrls.map((url, index) => (
            <Grid2
              key={`${url.slice(0, 20)}-${index}`}
              size={{ xs: 6, sm: 4, md: 3 }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  paddingTop: '100%',
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                }}
              >
                <img
                  src={url}
                  alt={`Preview ${index + 1}`}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                {onRemove && (
                  <IconButton
                    size="small"
                    onClick={(e) => handleRemoveImage(e, index)}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      backgroundColor: alpha(theme.palette.secondary.main, 0.6),
                      '&:hover': {
                        backgroundColor: alpha(
                          theme.palette.secondary.main,
                          0.8,
                        ),
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" sx={{ color: '#ffffff' }} />
                  </IconButton>
                )}
              </Box>
            </Grid2>
          ))}
        </Grid2>
      )}
      {!isDisabled && (
        <Box
          {...getRootProps()}
          sx={{
            width: '100%',
            border: '2px dashed',
            borderColor:
              isDragReject || isTooManyFilesError ? 'error.main' : 'grey.500',
            padding: 2,
            textAlign: 'center',
            borderRadius: 2,
            cursor: 'pointer',
            backgroundColor: isDragActive ? 'action.hover' : 'transparent',
            '&:hover': {
              borderColor: 'primary.main',
            },
          }}
        >
          <input {...getInputProps()} />
          <Typography color={'text.secondary'}>
            {isDragActive
              ? 'Solte as imagens aqui...'
              : 'Arraste e solte ou clique para selecionar'}
          </Typography>
          {isDragReject && (
            <Typography variant="body2" color="error" mt={1}>
              Um ou mais arquivos não são suportados.
            </Typography>
          )}
          {isTooManyFilesError && (
            <Typography variant="body2" color="error" mt={1}>
              Você só pode enviar até {maxFiles}{' '}
              {maxFiles > 1 ? 'imagens' : 'imagem'}.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ImageUploaderComponent;
